
import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Check, X } from "lucide-react";
import MphLogo from "../../../assets/MphLogo.png";
import RecruiterLogo from "../../../assets/RecruiterLogo.png";
import { Login } from "../../../supports/login";
import { useSearchParams } from "react-router-dom";
import { useToast } from "../../../common/toastSystem";
import { generateRequestId } from '../../../common/common';
import { useLocation } from 'react-router-dom';
export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    level: 0,
    label: "",
    color: "",
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [isResetApiLoading, setIsResetApiLoading] = useState(false);  
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
    const location = useLocation();
    const isResumeLogin = location.pathname === "/resumeResetPassword";
  const toast = useToast();
  // Password strength calculation
  useEffect(() => {
    if (!newPassword.trim()) {
      setPasswordStrength({ level: 0, label: "", color: "" });
      return;
    }

    let strength = 0;
    const checks = {
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    };

    // Calculate strength
    if (checks.length) strength++;
    if (checks.uppercase) strength++;
    if (checks.lowercase) strength++;
    if (checks.number) strength++;
    if (checks.special) strength++;

    // Set strength level
    let level, label, color;
    if (strength <= 2) {
      level = 1;
      label = "Weak";
      color = "bg-red-500";
    } else if (strength === 3) {
      level = 2;
      label = "Fair";
      color = "bg-orange-500";
    } else if (strength === 4) {
      level = 3;
      label = "Good";
      color = "bg-yellow-500";
    } else {
      level = 4;
      label = "Strong";
      color = "bg-green-500";
    }

    setPasswordStrength({ level, label, color });
  }, [newPassword]);

  // Validation
  const validatePasswords = () => {
    const errors = [];

    // if (newPassword.length < 8) {
    //   errors.push("Password must be at least 8 characters long");
    // }
    // if (!/[A-Z]/.test(newPassword)) {
    //   errors.push("Password must contain at least one uppercase letter");
    // }
    // if (!/[a-z]/.test(newPassword)) {
    //   errors.push("Password must contain at least one lowercase letter");
    // }
    // if (!/[0-9]/.test(newPassword)) {
    //   errors.push("Password must contain at least one number");
    // }
    // if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
    //   errors.push("Password must contain at least one special character");
    // }

    // setValidationErrors(errors);

    const match = newPassword === confirmPassword;
    setPasswordsMatch(match);

    return errors.length === 0 && match;
  };

  const handleSubmit = async () => {
    if (!validatePasswords()) return;
    if (!token) {
      toast.warning("Invalid or missing reset token");
      return;
    }

    try {
      const payload = {
        token: token,
        new_password: newPassword?.trim(),
        user_payload: {
          request_id: generateRequestId(),
          email: "ABC",
          tenant: "ABC",
        },
      };

      const response = await Login.resetPassword(payload);

      if (response?.success) {
        toast.success("Password reset successfully!");
        // redirect to login
        if(isResumeLogin){
            window.location.href = "/resume-login";
        }else{
            window.location.href = "/login";
        }
       
      } else {
        toast.warning(response?.message || "Something went wrong");
      }
    } catch (error) {
      console.error(error);
      toast.warning("Error resetting password");
    }
    finally {
      setIsResetApiLoading(false);
    }
  };

  const getPasswordRequirements = () => {
    return [
      { text: "At least 8 characters", met: newPassword.length >= 8 },
      { text: "One uppercase letter", met: /[A-Z]/.test(newPassword) },
      { text: "One lowercase letter", met: /[a-z]/.test(newPassword) },
      { text: "One number", met: /[0-9]/.test(newPassword) },
      {
        text: "One special character",
        met: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      },
    ];
  };

  return (
    <div className="min-h-screen main-background relative overflow-hidden">
      {/* Top colored bar */}
      <div className="w-full nav-bar-bg">
        <div className="flex justify-between items-center w-full px-8 py-4">
          {/* Logos */}
          <div className="flex items-center gap-4">
            <img src={MphLogo} alt="MPH Company Logo" className="h-12" />
            <img
              src={RecruiterLogo}
              alt="Recruiter Logo"
              className="w-16 mb-1"
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex items-center justify-center h-[calc(100vh-80px)] py-4 px-4">
        <div className="w-full max-w-2xl">
          {/* Reset card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-6">
            <h1 className="text-base font-semibold text-gray-700 mb-3">
              Reset your password
            </h1>

            {/* Info banner */}
            <div className="bg-blue-50 border border-blue-200 px-4 py-1.5 mb-3 rounded">
              <p className="text-sm text-gray-600">
                Please enter your new password below
              </p>
            </div>

            {/* New Password field */}
            <div className="mb-3">
              <label
                htmlFor="newPassword"
                className="block text-sm font-semibold text-gray-700 mb-1"
              >
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full text-sm px-3 py-1.5 pr-10 border border-gray-300 rounded focus:border-transparent"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">
                    Password Strength:
                  </span>
                  <span
                    className={`text-xs font-medium ${
                      passwordStrength.label === "Weak"
                        ? "text-red-600"
                        : passwordStrength.label === "Fair"
                        ? "text-orange-600"
                        : passwordStrength.label === "Good"
                        ? "text-yellow-600"
                        : "text-green-600"
                    }`}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((bar) => (
                    <div
                      key={bar}
                      className={`h-1 flex-1 rounded ${
                        bar <= passwordStrength.level
                          ? passwordStrength.color
                          : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Password Requirements
            {newPassword && (
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                <div className="space-y-1">
                  {getPasswordRequirements().map((req, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {req.met ? (
                        <Check size={14} className="text-green-600" />
                      ) : (
                        <X size={14} className="text-gray-400" />
                      )}
                      <span className={`text-xs ${req.met ? 'text-green-700' : 'text-gray-600'}`}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* Confirm Password field */}
            <div className="mb-4 relative">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-gray-700 mb-1"
              >
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordsMatch(true);
                  }}
                  className={`w-full text-sm px-3 py-1.5 pr-10 border rounded  ${
                    confirmPassword && !passwordsMatch
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
              {!passwordsMatch && (
                <p className="absolute text-xs text-red-600 mt-1">
                  Passwords do not match
                </p>
              )}
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && confirmPassword && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-[0.5rem] font-medium text-red-800 mb-1">
                  Please fix the following:
                </p>
                <ul className="list-disc list-inside text-[0.5rem] text-red-700 space-y-0.5">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Submit button */}
            <div className="flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={!newPassword?.trim() || !confirmPassword || isResetApiLoading}
                className={`profiler-bg disabled:bg-gray-300 disabled:cursor-not-allowed text-sm text-white px-6 py-2 rounded transition-all duration-200 font-medium ${
                  !newPassword?.trim() || !confirmPassword || isResetApiLoading
                }:'opacity-50 cursor-not-allowed':''}`}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
